import React, { useEffect, useState } from "react";
import { loadStyles } from "../../utils/cssLoader";
import { getData } from "../../utils/getData";
import { getImageUrl } from "../../utils/generalLiveCenter";
import { useSocket } from "../../hooks/useSocket";

/**
 * RugbyMatchHead — a self-contained, live rugby match-header widget.
 *
 * Adapts to match state via a single status resolver (never scatters `st === …` checks):
 *   • Upcoming (NSY)                 → kickoff label + "VS" + live countdown
 *   • Live (Live/1st half/2nd half…) → red status tag + running score
 *   • Finished / Finished AP / AET   → "Finished" / "FAP" / "FAET" + final score, winner bold
 *   • Abandoned / Postponed / …      → the off-status word, final/known score
 *
 * Data: rugby/match/{id}/detail → `.data` (competitors, ft/cfs, dt, tournament, venue, …).
 * Live: socket channel "RugbyMatch", event "RugbyMatchDetails-{id}" → payload replaces match.
 */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad2 = (n) => String(n).padStart(2, "0");

// Feed `dt` is a UTC wall-clock string with no zone suffix — append "Z" so it parses as UTC,
// then read it in the viewer's device timezone (or an explicit +N offset) for a correct instant.
const kickoffMs = (dt) => {
  if (!dt) return NaN;
  let s = String(dt).trim().replace(" ", "T");
  if (!/[zZ]$|[+\-]\d{2}:?\d{2}$/.test(s)) s += "Z";
  return new Date(s).getTime();
};
const kickoffParts = (dt, difference) => {
  const base = kickoffMs(dt);
  if (isNaN(base)) return null;
  const offH = parseInt(String(difference ?? "").replace("+", ""), 10) || 0;
  const d = offH !== 0 ? new Date(base + offH * 3600000) : new Date(base);
  const g = offH !== 0
    ? { date: d.getUTCDate(), month: d.getUTCMonth(), year: d.getUTCFullYear(), h: d.getUTCHours(), m: d.getUTCMinutes() }
    : { date: d.getDate(), month: d.getMonth(), year: d.getFullYear(), h: d.getHours(), m: d.getMinutes() };
  return g;
};
const formatMatchDate = (dt, difference) => {
  const p = kickoffParts(dt, difference);
  return p ? `${p.date} ${MONTHS[p.month]} ${p.year}` : "";
};
const kickoffLabel = (dt, difference) => {
  const p = kickoffParts(dt, difference);
  return p ? `${p.date} ${MONTHS[p.month]} · ${pad2(p.h)}:${pad2(p.m)}` : "Upcoming";
};

const splitScore = (s) =>
  typeof s === "string" && s.includes("-") ? s.split("-").map((x) => x.trim()) : [null, null];

// ── Rugby status resolver — single source of truth for every scenario ──────────
const RUGBY_LIVE = ["live", "1st half", "2nd half", "half time", "inprogress", "et 1st half", "et 2nd half"];
const RUGBY_FINISHED = { finished: "Finished", "finished ap": "FAP", "finished aet": "FAET", "full time": "Finished" };
const RUGBY_OFF = {
  cancelled: "Cancelled",
  postponed: "Postponed",
  abandoned: "Abandoned",
  deleted: "Cancelled",
  interrupted: "Interrupted",
  "result only": "Result",
};

const resolveRugbyStatus = (stRaw) => {
  const st = String(stRaw || "").trim();
  const low = st.toLowerCase();
  if (!st || low === "nsy") return { phase: "upcoming", label: "Upcoming", live: false };
  if (RUGBY_LIVE.includes(low)) return { phase: "live", label: "Live", live: true };
  if (RUGBY_FINISHED[low]) return { phase: "finished", label: RUGBY_FINISHED[low], live: false };
  if (RUGBY_OFF[low]) return { phase: "off", label: RUGBY_OFF[low], live: false };
  return { phase: "finished", label: st, live: false }; // safe fallback: show the raw word
};

const matchFromRaw = (raw) => raw?.data ?? raw ?? null;

// ── Small pieces ───────────────────────────────────────────────────────────────
const FormDots = ({ form = [] }) => {
  if (!form?.length) return null;
  return (
    <div className="rmh__form">
      {form.slice(0, 5).reverse().map((r, i) => {
        const k = String(r || "").toUpperCase();
        const mod = k === "W" ? "w" : k === "L" ? "l" : "d";
        return (
          <span key={i} className={`rmh__dot rmh__dot--${mod}`} title={k}>{k || mod.toUpperCase()}</span>
        );
      })}
    </div>
  );
};

const Flag = ({ flag, name }) => (
  <span className="rmh__flag">
    {typeof flag === "string" ? <img src={flag} alt={name ? `${name} logo` : ""} /> : flag || null}
  </span>
);

const Countdown = ({ targetMs, whenText }) => {
  const calc = () => {
    const diff = targetMs - Date.now();
    if (isNaN(diff) || diff <= 0) return { d: 0, h: 0, m: 0, s: 0 };
    return {
      d: Math.floor(diff / 86400000),
      h: Math.floor((diff / 3600000) % 24),
      m: Math.floor((diff / 60000) % 60),
      s: Math.floor((diff / 1000) % 60),
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetMs]);
  const cell = (n, l) => (
    <div className="rmh__cd-cell">
      <span className="rmh__cd-num">{String(n).padStart(2, "0")}</span>
      <span className="rmh__cd-lbl">{l}</span>
    </div>
  );
  return (
    <div className="rmh__countdown">
      <span className="rmh__cd-title">Kicks off in</span>
      <div className="rmh__cd-cells">
        {cell(t.d, "Days")}<span className="rmh__cd-sep">:</span>
        {cell(t.h, "Hrs")}<span className="rmh__cd-sep">:</span>
        {cell(t.m, "Min")}<span className="rmh__cd-sep">:</span>
        {cell(t.s, "Sec")}
      </div>
      {whenText ? <span className="rmh__cd-when">{whenText}</span> : null}
    </div>
  );
};

const Svg = ({ children }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const ICONS = {
  date: (<Svg><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></Svg>),
  venue: (<Svg><path d="M12 21s-7-6.4-7-11a7 7 0 0 1 14 0c0 4.6-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></Svg>),
  referee: (<Svg><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></Svg>),
};

// ── Main component ───────────────────────────────────────────────────────────
const RugbyMatchHead = ({
  serverData = null,
  data: dataProp = null,
  dataId,
  baseUrl,
  sport = "rugby",
  difference = "+0",
  socketUrl,
  channel = "RugbyMatch",
  event,
  loadCSS = true,
  dataClient = false,
}) => {
  // Always load the widget's stylesheet — SSR passes loadCSS=false (it inlines interactives/
  // editorial CSS only), so gating on loadCSS would leave this standalone widget unstyled.
  useEffect(() => {
    loadStyles("rugbyMatchHead");
  }, []);

  const [match, setMatch] = useState(() => matchFromRaw(serverData || dataProp));

  // SSR props → state
  useEffect(() => {
    const m = matchFromRaw(serverData || dataProp);
    if (m) setMatch(m);
  }, [serverData, dataProp]);

  // Hydration: re-read serialized data off the container
  useEffect(() => {
    if (!dataClient) return;
    try {
      const raw = document.querySelector(".microservice-cont")?.getAttribute("data-listingdata");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const m = parsed?.headData?.data || parsed?.data?.data || parsed?.data;
      if (m) setMatch(m);
    } catch (_) {}
  }, [dataClient]);

  // Embed: self-fetch the match detail
  useEffect(() => {
    if (dataClient || match || !dataId || !baseUrl) return;
    let cancelled = false;
    getData({ dataId, dataType: "rugby-match-detail", baseUrl, sport, difference }).then((res) => {
      if (cancelled) return;
      const m = matchFromRaw(res);
      if (m) setMatch(m);
    });
    return () => {
      cancelled = true;
    };
  }, [dataClient, match, dataId, baseUrl, sport, difference]);

  // Real-time: subscribe to the rugby match socket; payload replaces the match object.
  const resolvedSocketUrl = socketUrl || process.env.REACT_APP_SOCKET || "https://socket-bolsaas.6lgx.com/";
  const socket = useSocket(resolvedSocketUrl);
  useEffect(() => {
    if (!socket || !dataId) return;
    const evName = event || `RugbyMatchDetails-${dataId}`;
    const handler = (e) => {
      const incoming = matchFromRaw(e?.data) || e?.data;
      if (!incoming) return;
      if (incoming.id != null && String(incoming.id) !== String(dataId)) return;
      setMatch((prev) => ({ ...(prev || {}), ...incoming }));
    };
    socket.channel(channel)?.listen(evName, handler);
    return () => {
      try {
        socket.channel(channel)?.stopListening(evName, handler);
      } catch (_) {}
    };
  }, [socket, dataId, channel, event]);

  if (!match) {
    return (
      <div className="rmh rmh--loading">
        <span>Loading match…</span>
      </div>
    );
  }

  const c = match.competitors || {};
  const { phase, label, live } = resolveRugbyStatus(match.st);
  const isUpcoming = phase === "upcoming";

  // Running/final score: cfs (current) while live, else ft (full-time), else fs.
  const scoreStr = (live ? match.cfs : match.ft) || match.ft || match.cfs || match.fs || "";
  const [hs, as] = splitScore(scoreStr);

  // Winner (finished/off with a decided score): higher side wins; equal = draw (no winner).
  let winnerSide = null;
  if (!isUpcoming && !live) {
    const nh = parseInt(hs, 10);
    const na = parseInt(as, 10);
    if (!isNaN(nh) && !isNaN(na)) winnerSide = nh > na ? "home" : na > nh ? "away" : null;
  }

  const home = {
    name: c.htn,
    flag: c.htid ? getImageUrl("", "", sport, c.htid) : null,
    score: hs ?? "-",
    form: c.htf,
  };
  const away = {
    name: c.atn,
    flag: c.atid ? getImageUrl("", "", sport, c.atid) : null,
    score: as ?? "-",
    form: c.atf,
  };

  const competition = match.tournament?.nm || match.league?.nm || "";
  const stage = match.ro || "";
  const title = [competition, stage].filter(Boolean).join(" · ");

  // League logo — country flag keyed by the tournament's fid (repo convention).
  const leagueFid = match.tournament?.fid || match.tournament?.football_category?.fid || match.league?.fid;
  const leagueLogo = leagueFid
    ? `${process.env.REACT_APP_BASE_URL}/public/assets/images/flags/country_${leagueFid}.png`
    : null;

  const meta = {
    date: formatMatchDate(match.dt, difference),
    venue: match.venue?.name || match.std || "",
    referee: match.match_official?.official?.name || "",
  };
  const hasFooter = meta.date || meta.venue || meta.referee;

  const cdTargetMs = isUpcoming ? kickoffMs(match.dt) : NaN;

  return (
    <div className={`rmh rmh--${phase}`}>
      {/* Top row */}
      <div className="rmh__top">
        <span className="rmh__competition">
          {leagueLogo ? (
            <img
              className="rmh__comp-logo"
              src={leagueLogo}
              alt=""
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : null}
          <span className="rmh__comp-name">{title}</span>
        </span>
        {!isUpcoming && label ? (
          <span className={`rmh__status${live ? " rmh__status--live" : ""}`}>{label}</span>
        ) : null}
      </div>

      {/* Teams + score */}
      <div className="rmh__main">
        <div className={`rmh__team${winnerSide === "home" ? " rmh__team--win" : ""}`}>
          <Flag flag={home.flag} name={home.name} />
          <span className="rmh__name">{home.name}</span>
          <FormDots form={home.form} />
        </div>

        <div className="rmh__score">
          {isUpcoming ? (
            <span className="rmh__vs">VS</span>
          ) : (
            <div className="rmh__score-line">
              <span className={`rmh__score-num${winnerSide === "home" ? " rmh__score-num--win" : ""}`}>
                {home.score ?? "-"}
              </span>
              <span className="rmh__score-sep">–</span>
              <span className={`rmh__score-num${winnerSide === "away" ? " rmh__score-num--win" : ""}`}>
                {away.score ?? "-"}
              </span>
            </div>
          )}
          {match.ht && !isUpcoming ? <span className="rmh__ht">HT {match.ht}</span> : null}
        </div>

        <div className={`rmh__team${winnerSide === "away" ? " rmh__team--win" : ""}`}>
          <Flag flag={away.flag} name={away.name} />
          <span className="rmh__name">{away.name}</span>
          <FormDots form={away.form} />
        </div>
      </div>

      {/* Upcoming → countdown */}
      {isUpcoming && !isNaN(cdTargetMs) && (
        <Countdown targetMs={cdTargetMs} whenText={kickoffLabel(match.dt, difference)} />
      )}

      {/* Footer meta */}
      {hasFooter && (
        <div className="rmh__footer">
          {meta.date && <span className="rmh__meta">{ICONS.date}{meta.date}</span>}
          {meta.venue && <span className="rmh__meta">{ICONS.venue}{meta.venue}</span>}
          {meta.referee && <span className="rmh__meta">{ICONS.referee}{meta.referee}</span>}
        </div>
      )}
    </div>
  );
};

export default RugbyMatchHead;
